import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Organigramme.css";
import { getOrganigrammeRequest } from "../../../redux/actions/entitesActions";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { Search, X, Users, ChevronDown, ChevronRight, ArrowLeft } from "lucide-react";
import { API_BASE_URL } from "../../../../api";

const HIDDEN_ENTITY_IDS = new Set([]);
const HIDDEN_ENTITY_NAMES = new Set(["administration systeme", "administration système"]);

function normalizeEntityName(name = "") {
    return name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
}

function isHiddenEntity(entity) {
    return HIDDEN_ENTITY_IDS.has(Number(entity?.id)) || HIDDEN_ENTITY_NAMES.has(normalizeEntityName(entity?.name));
}

function filterHiddenEntities(tree = []) {
    return tree
        .filter((node) => !isHiddenEntity(node))
        .map((node) => ({
            ...node,
            children: filterHiddenEntities(node.children || []),
        }));
}

function flatEntities(tree = []) {
    const out = [];
    const walk = (node) => { out.push(node); (node.children || []).forEach(walk); };
    tree.forEach(walk);
    return out;
}

function entityCodeStartsWith(entity, prefix) {
    return String(entity?.code || "").trim().toUpperCase().startsWith(prefix);
}

function findEntityById(tree = [], id) {
    if (!id || id === "ALL") return null;
    const idNum = Number(id);
    for (const node of tree) {
        if (Number(node.id) === idNum) return node;
        const found = findEntityById(node.children || [], id);
        if (found) return found;
    }
    return null;
}

function buildDirectReportsIndex(allUsers = []) {
    const idx = new Map();
    allUsers.forEach((u) => {
        const mid = u.managerId || u.manager?.id;
        if (!mid) return;
        idx.set(mid, (idx.get(mid) || 0) + 1);
    });
    return idx;
}

function fullName(u) {
    if (!u) return "";
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.fullName || u.email || "—";
}

function normalizeUser(u) {
    if (!u) return null;
    return {
        id: u.id,
        fullName: fullName(u),
        email: u.email,
        phone: u.phone,
        city: u.city,
        profilePictureUrl: u.profilePictureUrl,
        jobName: u.job?.name || u.jobName || null,
        jobCode: u.job?.code || u.jobCode || null,
        managerId: u.manager?.id || u.managerId || null,
        entityName: u.entityName || u.entity?.name || null,
        entityCode: u.entityCode || u.entity?.code || null,
    };
}

function Avatar({ url, name, size = 36 }) {
    const initials = (name || "U")[0]?.toUpperCase();
    const imgUrl = url ? (url.startsWith("http") ? url : `${API_BASE_URL}/api/users/images/profile/${url}`) : null;
    return (
        <span className="av" style={{ "--av-size": `${size}px` }}>
            {imgUrl
                ? <img src={imgUrl} alt={name}
                       onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                : null
            }
            <span className="av__initials" style={{ display: imgUrl ? "none" : "flex" }}>{initials}</span>
        </span>
    );
}

export default function Organigramme() {
    const dispatch = useDispatch();
    const { organigramme, organigrammeError, entitesLoading } = useSelector((s) => s.entite);
    const { users, loading: userLoading } = useSelector((s) => s.user);

    const [departmentId, setDepartmentId] = useState("ALL");
    const [serviceId, setServiceId] = useState("ALL");
    const [sectionId, setSectionId] = useState("ALL");
    const [horizontal, setHorizontal] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [panelHistory, setPanelHistory] = useState([]);
    const [expanded, setExpanded] = useState(() => new Set());

    const toggleExpand = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    useEffect(() => {
        dispatch(getOrganigrammeRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch]);

    const orgTree = useMemo(() => filterHiddenEntities(organigramme || []), [organigramme]);

    useEffect(() => {
        if (orgTree.length > 0) {
            setExpanded(new Set(flatEntities(orgTree).map((entity) => entity.id)));
        }
    }, [orgTree]);

    const allEntities = useMemo(() => flatEntities(orgTree), [orgTree]);

    const departmentOptions = useMemo(() => [
        { id: "ALL", name: "Tous les départements" },
        ...orgTree
            .filter((e) => entityCodeStartsWith(e, "ENT"))
            .map((e) => ({ id: String(e.id), name: e.name }))
    ], [orgTree]);

    const selectedDepartment = useMemo(() => findEntityById(orgTree, departmentId), [orgTree, departmentId]);
    const serviceScope = useMemo(() => selectedDepartment ? flatEntities([selectedDepartment]) : allEntities, [selectedDepartment, allEntities]);

    const serviceOptions = useMemo(() => [
        { id: "ALL", name: "Tous les services" },
        ...serviceScope
            .filter((e) => entityCodeStartsWith(e, "SER"))
            .map((e) => ({ id: String(e.id), name: e.name }))
    ], [serviceScope]);

    const selectedService = useMemo(() => findEntityById(orgTree, serviceId), [orgTree, serviceId]);
    const sectionScope = useMemo(() => selectedService ? flatEntities([selectedService]) : serviceScope, [selectedService, serviceScope]);

    const sectionOptions = useMemo(() => [
        { id: "ALL", name: "Toutes les sections" },
        ...sectionScope
            .filter((e) => entityCodeStartsWith(e, "SEC"))
            .map((e) => ({ id: String(e.id), name: e.name }))
    ], [sectionScope]);

    useEffect(() => {
        if (departmentId !== "ALL" && !departmentOptions.some((o) => o.id === departmentId)) {
            setDepartmentId("ALL");
            setServiceId("ALL");
            setSectionId("ALL");
        }
    }, [departmentId, departmentOptions]);

    useEffect(() => {
        if (serviceId !== "ALL" && !serviceOptions.some((o) => o.id === serviceId)) {
            setServiceId("ALL");
            setSectionId("ALL");
        }
    }, [serviceId, serviceOptions]);

    useEffect(() => {
        if (sectionId !== "ALL" && !sectionOptions.some((o) => o.id === sectionId)) {
            setSectionId("ALL");
        }
    }, [sectionId, sectionOptions]);

    const handleDepartmentChange = (value) => {
        setDepartmentId(value);
        setServiceId("ALL");
        setSectionId("ALL");
    };

    const handleServiceChange = (value) => {
        setServiceId(value);
        setSectionId("ALL");
    };

    const visibleTree = useMemo(() => {
        const activeId = sectionId !== "ALL" ? sectionId : serviceId !== "ALL" ? serviceId : departmentId;
        if (activeId === "ALL") return orgTree;
        const selected = findEntityById(orgTree, activeId);
        return selected ? [selected] : [];
    }, [orgTree, departmentId, serviceId, sectionId]);

    const allMembersFlat = useMemo(() => {
        const out = [];
        const walk = (node) => {
            (node.members || []).forEach((m) => out.push({ ...m, __entity: node }));
            (node.children || []).forEach(walk);
        };
        visibleTree.forEach(walk);
        return out;
    }, [visibleTree]);

    const searchMatches = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return [];
        return allMembersFlat.filter((m) => {
            return (m.fullName || "").toLowerCase().includes(q)
                || (m.email || "").toLowerCase().includes(q)
                || (m.city || "").toLowerCase().includes(q);
        }).slice(0, 8);
    }, [search, allMembersFlat]);

    const directReportsIndex = useMemo(() => buildDirectReportsIndex(users || []), [users]);
    const userById = useMemo(() => {
        const map = new Map();
        (users || []).forEach((u) => map.set(u.id, u));
        return map;
    }, [users]);

    const openEntityPanel = (entity) => {
        setPanelHistory([]);
        setSelectedUser(null);
        setSelectedEntity(entity);
    };
    const openUserPanel = (user, entity) => {
        setPanelHistory((previous) => [...previous, { entity: selectedEntity, user: selectedUser }]);
        setSelectedEntity(null);
        setSelectedUser({ ...user, __entity: entity || user.__entity });
    };
    const closePanels = () => {
        setPanelHistory([]);
        setSelectedEntity(null);
        setSelectedUser(null);
    };
    const goBackPanel = () => {
        setPanelHistory((previous) => {
            const lastPanel = previous[previous.length - 1];
            setSelectedEntity(lastPanel?.entity || null);
            setSelectedUser(lastPanel?.user || null);
            return previous.slice(0, -1);
        });
    };

    const isLoading = !!entitesLoading || !!userLoading;

    return (
        <div className="org">
            {/* TOOLBAR */}
            <div className="org__toolbar">
                <div className="org__toolbarLeft">
                    {/*<button className="org__backBtn" type="button" onClick={() => window.history.back()}>*/}
                    {/*    <ArrowLeft size={16} />*/}
                    {/*</button>*/}
                    <div>
                        <h1 className="org__title">Organigramme</h1>
                        {/*<p className="org__subtitle">101 collaborateurs · {allEntities.length} départements</p>*/}
                    </div>
                </div>
                <div className="org__toolbarRight">
                    {/* Search */}
                    <div className="org__searchWrap">
                        <Search className="org__searchIcon" size={14} />
                        <input
                            className="org__search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher un collaborateur…"
                        />
                        {search && (
                            <button className="org__searchClear" onClick={() => setSearch("")} type="button">
                                <X size={12} />
                            </button>
                        )}
                        {!!searchMatches.length && (
                            <div className="org__suggest">
                                {searchMatches.map((m) => (
                                    <button key={`${m.id}-${m.__entity?.id}`} type="button"
                                            className="org__suggestItem"
                                            onClick={() => { openUserPanel(m, m.__entity); setSearch(""); }}>
                                        <Avatar url={m.profilePictureUrl} name={m.fullName} size={30} />
                                        <div>
                                            <div className="org__suggestName">{m.fullName}</div>
                                            <div className="org__suggestMeta">{m.__entity?.name} · {m.jobName || "—"}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <select className="org__select" value={departmentId} onChange={(e) => handleDepartmentChange(e.target.value)}>
                        {departmentOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>

                    {/*<select className="org__select" value={serviceId} onChange={(e) => handleServiceChange(e.target.value)}>*/}
                    {/*    {serviceOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}*/}
                    {/*</select>*/}

                    {/*<select className="org__select" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>*/}
                    {/*    {sectionOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}*/}
                    {/*</select>*/}

                    {/* Toggle */}
                    {/*<label className="org__toggleWrap">*/}
                    {/*    /!*<span className="org__toggleLabel">Horizontal</span>*!/*/}
                    {/*    <div className="org__switch">*/}
                    {/*        <input type="checkbox" checked={horizontal} onChange={(e) => setHorizontal(e.target.checked)} />*/}
                    {/*        <span className="org__slider" />*/}
                    {/*    </div>*/}
                    {/*</label>*/}
                </div>
            </div>

            {/* CANVAS */}
            <div className={`org__canvas ${horizontal ? "is-horizontal" : ""}`}>
                {isLoading && <div className="org__hint">Chargement des données…</div>}
                {organigrammeError && <div className="org__error">{organigrammeError}</div>}
                <OrgTree
                    nodes={visibleTree}
                    expanded={expanded}
                    onToggleExpand={toggleExpand}
                    onOpenEntity={openEntityPanel}
                    onOpenUser={openUserPanel}
                    userById={userById}
                />
            </div>

            {/* MODAL */}
            {(selectedEntity || selectedUser) && (
                <div className="orgModal" role="dialog" aria-modal="true">
                    <div className="orgModal__overlay" onClick={closePanels} />
                    <div className="orgModal__panel">
                        {selectedEntity && (
                            <EntityPanel entity={selectedEntity} onOpenUser={openUserPanel}
                                         directReportsIndex={directReportsIndex} onClose={closePanels}
                                         userById={userById} />
                        )}
                        {selectedUser && (
                            <UserPanel user={selectedUser} users={users || []}
                                       directReportsIndex={directReportsIndex} userById={userById}
                                       onOpenUser={openUserPanel} onClose={closePanels}
                                       onBack={panelHistory.length ? goBackPanel : null} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ────────────────────────────────────────────────────────────────
   ARBRE — layout "par rangs" : toutes les cartes d'un même niveau
   (département / service / section...) partagent le même `top`,
   quelle que soit la forme des branches voisines. La position
   horizontale de chaque carte est calculée à partir de ses feuilles,
   puis les connecteurs sont tracés en SVG entre parent et enfant.
   ──────────────────────────────────────────────────────────────── */

const CARD_W = 200;   // doit correspondre à --org-card-w / .otree-card__box width
const CARD_H = 84;    // doit correspondre à la hauteur fixe de .otree-card__box
const GAP_H = 40;     // espace horizontal entre deux cartes voisines
const ROW_H = 150;    // distance verticale entre le haut d'un niveau et le suivant

/**
 * Calcule, pour chaque nœud visible, sa position (x = colonne, level = rang)
 * ainsi que la liste des connecteurs parent → enfant à dessiner.
 * Les enfants d'un nœud replié (non "expanded") ne sont pas pris en compte,
 * exactement comme avant.
 */
function computeTreeLayout(nodes, expanded) {
    const positions = new Map();
    const edges = [];
    let cursor = 0;

    function visit(node, level) {
        const isOpen = expanded.has(node.id);
        const rawChildren = node.children || [];
        const children = isOpen ? rawChildren : [];

        let x;
        if (children.length === 0) {
            x = cursor;
            cursor += 1;
        } else {
            const childXs = children.map((child) => {
                edges.push({ parentId: node.id, childId: child.id });
                return visit(child, level + 1);
            });
            x = (childXs[0] + childXs[childXs.length - 1]) / 2;
        }

        positions.set(node.id, {
            x,
            level,
            node,
            hasChildren: rawChildren.length > 0,
            isOpen,
        });
        return x;
    }

    nodes.forEach((n) => visit(n, 0));

    return { positions, edges, leafCount: cursor };
}

function OrgTree({ nodes, expanded, onToggleExpand, onOpenEntity, onOpenUser, userById }) {
    const { positions, edges, leafCount } = useMemo(
        () => computeTreeLayout(nodes || [], expanded),
        [nodes, expanded]
    );

    if (!nodes?.length) return null;

    let maxLevel = 0;
    positions.forEach((p) => { if (p.level > maxLevel) maxLevel = p.level; });

    const totalWidth = Math.max(leafCount * (CARD_W + GAP_H) - GAP_H, CARD_W);
    const totalHeight = (maxLevel + 1) * ROW_H;

    const colToLeft = (x) => x * (CARD_W + GAP_H);
    const colCenterX = (x) => colToLeft(x) + CARD_W / 2;
    const levelTop = (level) => level * ROW_H;

    return (
        <div
            className="otree-canvas"
            style={{ position: "relative", width: totalWidth, height: totalHeight, marginTop: 40 }}
        >
            <svg
                width={totalWidth}
                height={totalHeight}
                style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", overflow: "visible" }}
            >
                {edges.map(({ parentId, childId }) => {
                    const p = positions.get(parentId);
                    const c = positions.get(childId);
                    if (!p || !c) return null;
                    const x1 = colCenterX(p.x);
                    const y1 = levelTop(p.level) + CARD_H;
                    const x2 = colCenterX(c.x);
                    const y2 = levelTop(c.level);
                    const midY = y1 + (y2 - y1) / 2;
                    const d = `M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`;
                    return (
                        <path
                            key={`${parentId}-${childId}`}
                            d={d}
                            fill="none"
                            stroke="#d1d5db"
                            strokeWidth="2"
                            strokeDasharray="5,4"
                        />
                    );
                })}
            </svg>

            {[...positions.values()].map(({ x, level, node, hasChildren, isOpen }) => (
                <div
                    key={node.id}
                    style={{
                        position: "absolute",
                        left: colToLeft(x),
                        top: levelTop(level),
                        width: CARD_W,
                    }}
                >
                    <OrgCard
                        node={node}
                        level={level}
                        hasChildren={hasChildren}
                        isOpen={isOpen}
                        onToggleExpand={onToggleExpand}
                        onOpenEntity={onOpenEntity}
                        onOpenUser={onOpenUser}
                        userById={userById}
                    />
                </div>
            ))}
        </div>
    );
}

function OrgCard({ node, level, hasChildren, isOpen, onToggleExpand, onOpenEntity, onOpenUser, userById }) {
    const lvClass = `otree-node--lv${Math.min(level, 3)}`;
    const levelLabel = level === 0 ? "Département" : level === 1 ? "Service" : "Section";
    const respPhoto = node.responsable ? (userById.get(node.responsable.id)?.profilePictureUrl || node.responsable.profilePictureUrl) : null;

    return (
        <div className={`otree-node ${lvClass}`}>
            <div className="otree-card">
                <div className="otree-card__box">
                    <button type="button" className="otree-card__main" onClick={() => onOpenEntity(node)}>
                        <div className="otree-card__top">
                            <div className="otree-card__name">{node.name}</div>
                            <span className="otree-card__badge">{levelLabel}</span>
                        </div>
                        <div className="otree-card__meta">
                            <span className="otree-card__pill">
                                <Users size={10} /> {(node.members || []).length} membre{(node.members || []).length !== 1 ? "s" : ""}
                            </span>
                            {node.responsable && (
                                <button type="button" className="otree-card__resp"
                                        onClick={(e) => { e.stopPropagation(); onOpenUser(node.responsable, node); }}>
                                    <Avatar url={respPhoto} name={node.responsable.fullName} size={16} />
                                    <span className="otree-card__respName">{node.responsable.fullName}</span>
                                </button>
                            )}
                        </div>
                    </button>
                    {hasChildren && (
                        <button className="otree-card__toggle" type="button" onClick={() => onToggleExpand(node.id)}>
                            {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ModalHeader({ onClose, children }) {
    return (
        <div className="orgModal__header">
            {children}
            <button className="orgModal__closeBtn" onClick={onClose} type="button" aria-label="Fermer">
                <X size={15} />
            </button>
        </div>
    );
}

function EntityPanel({ entity, onOpenUser, directReportsIndex, onClose, userById }) {
    const members = entity.members || [];
    const respPhoto = entity.responsable ? (userById.get(entity.responsable.id)?.profilePictureUrl || entity.responsable.profilePictureUrl) : null;
    return (
        <>
            <ModalHeader onClose={onClose}>
                <div className="orgModal__headerInfo">
                    <div className="orgModal__headerName">{entity.name}</div>
                    <div className="orgModal__headerSub">{members.length} membre{members.length !== 1 ? "s" : ""}</div>
                </div>
            </ModalHeader>
            <div className="orgModal__body">
                {entity.responsable && (
                    <div className="orgModal__section">
                        <div className="orgModal__sectionTitle">Responsable</div>
                        <button type="button" className="orgModal__personBtn"
                                onClick={() => onOpenUser(entity.responsable, entity)}>
                            <Avatar url={respPhoto} name={entity.responsable.fullName} size={40} />
                            <div>
                                <div className="orgModal__personName">{entity.responsable.fullName}</div>
                                <div className="orgModal__personRole">{entity.responsable.jobName || "—"}</div>
                            </div>
                            <ChevronRight size={14} className="orgModal__personArrow" />
                        </button>
                    </div>
                )}
                <div className="orgModal__section">
                    <div className="orgModal__sectionTitle">Membres · {members.length}</div>
                    <div className="orgModal__list">
                        {members.map((m) => {
                            const teamCount = directReportsIndex.get(m.id) || 0;
                            const memberPhoto = userById.get(m.id)?.profilePictureUrl || m.profilePictureUrl;
                            return (
                                <button key={m.id} type="button" className="orgModal__personBtn"
                                        onClick={() => onOpenUser(m, entity)}>
                                    <Avatar url={memberPhoto} name={m.fullName} size={38} />
                                    <div>
                                        <div className="orgModal__personName">{m.fullName}</div>
                                        <div className="orgModal__personRole">{m.jobName || m.jobCode || "—"}</div>
                                    </div>
                                    {teamCount > 0 && (
                                        <span className="orgModal__badge"><Users size={11} /> {teamCount}</span>
                                    )}
                                    <ChevronRight size={14} className="orgModal__personArrow" />
                                </button>
                            );
                        })}
                        {!members.length && <div className="orgModal__muted">Aucun membre.</div>}
                    </div>
                </div>
            </div>
        </>
    );
}

function UserPanel({ user, users, userById, onOpenUser, onClose, onBack }) {
    const dbUser = user?.id ? userById.get(user.id) : null;
    const current = useMemo(() => {
        const target = dbUser || user;
        return normalizeUser(target) || target;
    }, [user, dbUser]);

    const managerId = current.managerId || current.manager?.id;
    const managerFull = managerId ? userById.get(managerId) : null;
    const manager = managerFull ? normalizeUser(managerFull) : null;

    const teamMembers = useMemo(() => {
        return (users || [])
            .filter((u) => String(u.managerId || u.manager?.id) === String(current.id))
            .map(normalizeUser).filter(Boolean)
            .sort((a, b) => (a.fullName || "").localeCompare(b.fullName || "", "fr"));
    }, [users, current.id]);

    const imgUrl = current.profilePictureUrl
        ? (current.profilePictureUrl.startsWith("http")
            ? current.profilePictureUrl
            : `${API_BASE_URL}/api/users/images/profile/${current.profilePictureUrl}`)
        : null;

    const initials = `${(current.fullName || "U")[0]?.toUpperCase()}`;

    return (
        <>
            <div className="orgModal__userHeader">
                {onBack && (
                    <button className="orgModal__backBtn" onClick={onBack} type="button" aria-label="Retour">
                        <ArrowLeft size={16} />
                        <span>Retour</span>
                    </button>
                )}
                <button className="orgModal__closeBtn orgModal__closeBtn--light" onClick={onClose} type="button">
                    <X size={15} />
                </button>
                <div className="orgModal__userAvatar">
                    {imgUrl
                        ? <img src={imgUrl} alt=""
                               onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                        : null
                    }
                    <span style={{ display: imgUrl ? "none" : "flex" }}>{initials}</span>
                </div>
                <div className="orgModal__userName">{current.fullName}</div>
                <div className="orgModal__userJob">{current.jobName || current.jobCode || "—"}</div>
                <div className="orgModal__userDept">{user.__entity?.name || current.entityName || "—"}{current.city ? ` · ${current.city}` : ""}</div>
            </div>

            <div className="orgModal__body">
                <div className="orgModal__section">
                    <div className="orgModal__sectionTitle">Coordonnées</div>
                    <div className="orgModal__infoGrid">
                        <div className="orgModal__infoRow"><span className="orgModal__infoLabel">Email</span><span className="orgModal__infoVal">{current.email ? <a href={`mailto:${current.email}`} className="orgModal__link">{current.email}</a> : "—"}</span></div>
                        <div className="orgModal__infoRow"><span className="orgModal__infoLabel">Téléphone</span><span className="orgModal__infoVal">{current.phone ? <a href={`tel:${current.phone}`} className="orgModal__link">{current.phone}</a> : "—"}</span></div>
                        <div className="orgModal__infoRow"><span className="orgModal__infoLabel">Ville</span><span className="orgModal__infoVal">{current.city || "—"}</span></div>
                    </div>
                </div>

                {manager && (
                    <div className="orgModal__section">
                        <div className="orgModal__sectionTitle">Manager</div>
                        <button type="button" className="orgModal__personBtn"
                                onClick={() => onOpenUser({ ...manager, __entity: user.__entity })}>
                            <Avatar url={manager.profilePictureUrl} name={manager.fullName} size={38} />
                            <div>
                                <div className="orgModal__personName">{manager.fullName}</div>
                                <div className="orgModal__personRole">{manager.jobName || "—"}</div>
                            </div>
                            <ChevronRight size={14} className="orgModal__personArrow" />
                        </button>
                    </div>
                )}

                {teamMembers.length > 0 && (
                    <div className="orgModal__section">
                        <div className="orgModal__sectionTitle">Équipe · {teamMembers.length}</div>
                        <div className="orgModal__list">
                            {teamMembers.map((m) => (
                                <button key={m.id} type="button" className="orgModal__personBtn"
                                        onClick={() => onOpenUser({ ...m, __entity: user.__entity })}>
                                    <Avatar url={m.profilePictureUrl} name={m.fullName} size={38} />
                                    <div>
                                        <div className="orgModal__personName">{m.fullName}</div>
                                        <div className="orgModal__personRole">{m.jobName || "—"}</div>
                                    </div>
                                    <ChevronRight size={14} className="orgModal__personArrow" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
